import type {
    AgentTaskProposedChangeResultSet,
    AgentTaskWorkItemSummary,
} from '../types';
import { AgentTaskProposalVote } from '../types';
import { parseUtcDate } from '../helpers';

export type TaskDetailTabKey =
    | 'pull-requests'
    | 'proposed-changes'
    | 'job-executions'
    | 'clarifications'
    | 'profiles'
    | 'additional-context';

export const TASK_DETAIL_TAB_ORDER: TaskDetailTabKey[] = [
    'pull-requests',
    'proposed-changes',
    'job-executions',
    'clarifications',
    'profiles',
    'additional-context',
];

function parseTime(value?: string | null) {
    return parseUtcDate(value)?.getTime() ?? Number.NEGATIVE_INFINITY;
}

export function selectLatestWorkItem(workItems: AgentTaskWorkItemSummary[]) {
    return [...workItems].sort((left, right) =>
        parseTime(right.agentEndDate) - parseTime(left.agentEndDate)
        || parseTime(right.agentStartDate) - parseTime(left.agentStartDate)
        || right.workItemId - left.workItemId)[0];
}

export function calculateTaskImpact(
    proposalResult: AgentTaskProposedChangeResultSet | undefined,
    workItems: AgentTaskWorkItemSummary[],
) {
    const taskShare = proposalResult?.taskSnapshotInclusivePercent;
    const allTaskReduction = proposalResult?.expectedReductionPercent
        ?? selectLatestWorkItem(workItems)?.estimatedGain
        ?? undefined;
    const allSnapshotReduction = proposalResult?.expectedSnapshotReductionPercent
        ?? (taskShare !== undefined && allTaskReduction !== undefined
            ? taskShare * allTaskReduction / 100
            : undefined);
    const currentActiveProposals = proposalResult?.proposedChangeEvents.filter(change =>
        change.isCurrent && change.outcome !== 'discarded') ?? [];
    const hasVotes = currentActiveProposals.some(change => change.vote !== null);
    const okTaskReduction = hasVotes
        ? Math.min(100, currentActiveProposals.reduce((total, change) =>
            change.vote === AgentTaskProposalVote.Ok
            && change.expectedReductionPercent !== undefined
            && change.expectedReductionPercent !== null
            && Number.isFinite(change.expectedReductionPercent)
                ? total + Math.max(0, change.expectedReductionPercent)
                : total, 0))
        : undefined;
    const okSnapshotReduction = okTaskReduction !== undefined && taskShare !== undefined
        ? taskShare * okTaskReduction / 100
        : undefined;

    return {
        taskShare,
        allTaskReduction,
        allSnapshotReduction,
        hasVotes,
        okTaskReduction,
        okSnapshotReduction,
    };
}

export function getFirstRelevantTaskTab(
    relevantTabs: ReadonlySet<TaskDetailTabKey>,
) {
    return TASK_DETAIL_TAB_ORDER.find(tab => relevantTabs.has(tab));
}
